import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import CSS from 'csstype';
import PropTypes from 'prop-types';
import { CSSObject } from '../theming';

const breakpoints = tokens.viewports.breakpoints;

type Breakpoint = keyof typeof breakpoints;

export type CSSValue = string | number | Array<string | number>;

export type ResponsiveProp<T extends CSSValue | boolean> =
  | T
  | Partial<Record<Breakpoint | number, T>>;

export type BreakpointProp<K extends string, V> = Record<K, V> & Record<string, Record<K, V>>;

export const breakpointKeys = Object.keys(tokens.viewports.breakpoints) as Breakpoint[];

type Nullable<T> = T extends Array<infer A> ? Array<A | null | undefined> : T | null | undefined;

export const responsivePropType = <T extends CSSValue | boolean>(
  validator: PropTypes.Requireable<Nullable<T>>
) => {
  return (
    process.env.NODE_ENV !== 'production'
      ? PropTypes.oneOfType([
          validator,
          PropTypes.shape(
            breakpointKeys.reduce((result, breakpoint) => {
              result[breakpoint] = validator;
              return result;
            }, {} as { [K in Breakpoint]: PropTypes.Requireable<Nullable<T>> })
          ),
        ])
      : undefined
  ) as PropTypes.Validator<ResponsiveProp<T>>;
};

function renderValues(values: CSSValue, unit?: string) {
  if (!Array.isArray(values)) return unit ? `${values}${unit}` : values;

  return values.map((value) => (unit ? `${value}${unit}` : values)).join(' ');
}

// eslint reports false positive here
// eslint-disable-next-line
type NumberOrString<T> = T extends Array<infer I> ? string : NonNullable<T>;

/**
 *
 * ## Example 1
 *
 * Input:
 *
 * ```js
 * [{"xsmall": [16, 24], "small": [32, 48]]}, "padding", "px"]
 * ```
 *
 * Output:
 *
 * ```js
 * {
 *  "padding": "16px 24px",
 *  "@media(min-width: 600px)": {
 *    "padding": "16px 24px"
 *  },
 *  "@media(min-width: 900px)": {
 *    "padding": "32px 48px"
 *  }
 * }
 * ```
 *
 * ## Example 2
 *
 * Input:
 *
 * ```js
 * [16, "fontSize", "px"]
 * ```
 * Output:
 *
 * ```js
 * { "fontSize": "16px" }
 * ```
 *
 */
export function responsiveProp<
  T extends number | string | string[],
  K extends keyof CSS.Properties
>(values: ResponsiveProp<T> | undefined, property: K): BreakpointProp<K, NumberOrString<T>>;
export function responsiveProp<T extends number | number[], K extends keyof CSS.Properties>(
  values: ResponsiveProp<T> | undefined,
  property: K,
  unit: string
): BreakpointProp<K, string>;
export function responsiveProp<T extends CSSValue | boolean>(
  values: ResponsiveProp<T> | undefined,
  callback: (x: T) => CSSObject
): CSSObject;
export function responsiveProp<T extends CSSValue, K extends keyof CSS.Properties>(
  values: ResponsiveProp<T> | undefined,
  propertyOrCallback: (x: T) => CSSObject | K,
  unit?: string
) {
  if (values == null) return {};

  const callback =
    typeof propertyOrCallback === 'function'
      ? propertyOrCallback
      : (x: T) => ({
          [propertyOrCallback]: renderValues(x, unit),
        });

  if (typeof values === 'object' && !Array.isArray(values)) {
    if (Object.keys(values).length === 0) return {};

    const sortedBreakpoints = [];

    /*
      If the responsive prop is an object, we have to combine the named breakpoints
      with the custom breakpoints and then order them from smallest to biggest
      before setting them.
    */
    for (const [key, value] of Object.entries(values)) {
      // value can be a Boolean, so we allow `false`
      if (value !== undefined) {
        if (key in breakpoints) {
          const px = breakpoints[key as Breakpoint];
          sortedBreakpoints.push({ px: parseInt(px), value });
        } else {
          sortedBreakpoints.push({ px: parseInt(key), value });
        }
      }
    }

    sortedBreakpoints.sort((elementA, elementB) => {
      return elementA.px - elementB.px;
    });

    const result: CSSObject = callback(sortedBreakpoints[0].value);

    sortedBreakpoints.forEach((customBreakpointPair) => {
      result[`@media(min-width: ${customBreakpointPair.px}px)`] = callback(
        customBreakpointPair.value
      );
    });
    return result;
  } else {
    return callback(values as T);
  }
}

export default responsiveProp;
